/**
 * My Garage favourites — the vehicles a customer has saved, in the order they
 * rank them.
 *
 * Stored on the user's own profile under `privateData.garageFavourites` as an
 * ordered list of listing ids. Private scope, for the same reason as the
 * trade-in: what a customer shortlists is theirs, and `publicData` would expose
 * it on their public profile.
 *
 * The list order is the ranking. Position 0 is #1. Ranking is a customer
 * choice, so it is never derived from the AI fit score — that score lives
 * beside this list and does not reorder it.
 *
 * Ids are kept as opaque strings. A saved id can outlive its listing (sold,
 * removed, unpublished); callers resolve ids against the listings they have
 * loaded and skip the ones that no longer resolve, rather than this module
 * guessing at listing state.
 */

export const MAX_FAVOURITES = 25;

const isListingId = value => typeof value === 'string' && value.trim().length > 0;

/**
 * Reads the saved favourite ids off the current user, or an empty list.
 * @param {Object} currentUser
 * @returns {string[]}
 */
export const getFavouriteIds = currentUser => {
  const saved = currentUser?.attributes?.profile?.privateData?.garageFavourites;
  return normalizeFavouriteIds(saved?.ids);
};

/**
 * Cleans a list of ids read from the profile or from the UI: trims, drops
 * blanks and non-strings, removes duplicates keeping the first occurrence (so
 * the customer's chosen rank wins), and caps the length.
 * @param {*} ids
 * @returns {string[]}
 */
export const normalizeFavouriteIds = ids => {
  if (!Array.isArray(ids)) return [];
  const seen = new Set();
  const out = [];
  ids.forEach(raw => {
    if (!isListingId(raw)) return;
    const id = raw.trim();
    if (seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out.slice(0, MAX_FAVOURITES);
};

export const isFavourite = (ids, listingId) => normalizeFavouriteIds(ids).includes(listingId);

/**
 * Saves a vehicle at the bottom of the ranking, or removes it when it is
 * already saved. Returns a new array; the input is not mutated.
 *
 * Adding past MAX_FAVOURITES returns the list unchanged. The caller can compare
 * lengths to tell the customer why nothing was saved.
 * @param {string[]} ids
 * @param {string} listingId
 * @returns {string[]}
 */
export const toggleFavourite = (ids, listingId) => {
  const current = normalizeFavouriteIds(ids);
  if (!isListingId(listingId)) return current;
  const id = listingId.trim();

  if (current.includes(id)) return current.filter(x => x !== id);
  if (current.length >= MAX_FAVOURITES) return current;
  return [...current, id];
};

/**
 * Moves the vehicle at `from` to position `to`, shifting the others. Out of
 * range indexes return the list unchanged rather than throwing, so a stale
 * drag does not corrupt the ranking.
 * @param {string[]} ids
 * @param {number} from
 * @param {number} to
 * @returns {string[]}
 */
export const moveFavourite = (ids, from, to) => {
  const current = normalizeFavouriteIds(ids);
  const inRange = i => Number.isInteger(i) && i >= 0 && i < current.length;
  if (!inRange(from) || !inRange(to) || from === to) return current;

  const next = [...current];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * The record written to the profile. The timestamp lets a later reader tell
 * which of two tabs wrote last, if it ever matters.
 * @param {string[]} ids
 * @param {Date} [now]
 * @returns {{ids: string[], updatedAt: string}}
 */
export const toFavouriteRecord = (ids, now = new Date()) => ({
  ids: normalizeFavouriteIds(ids),
  updatedAt: now.toISOString(),
});
